import { translate } from '../../Internationalization';
import React from 'react';

/**
 * Registry to preserve the external iframe across React lifecycle flickers
 * and navigation events. This avoids re-loading the script/iframe constantly.
 */
/** @type {HTMLIFrameElement | null} */
let cachedPortalIframe = null;
/** @type {'idle' | 'loading' | 'loaded'} */
let portalScriptState = 'idle';
const conciergeSelfPortalUrl = process.env.REACT_APP_CONCIERGE_SELF_PORTAL_URL;

/**
 * @typedef {Readonly<{ withTitle?: boolean }>} Props
 */

/** @extends {React.Component<Props, {}>} */
export default class MoveConciergePortal extends React.Component {
	/**
	 * @returns {void}
	 */
	componentDidMount() {
		this.#initializePortal();
	}

	/**
	 * @returns {void}
	 */
	componentWillUnmount() {
		/**
		 * before React destroys the container. This keeps the iframe "alive"
		 * and prevents the "disappear" issue on return.
		 */
		const container = document.getElementById('mc-embed');
		if (!container || !this.props) {
			return;
		}

		// Find the iframe injected by the external script
		const iframe = container.querySelector('iframe');
		if (iframe) {
			cachedPortalIframe = iframe;
			// Detach from the disappearing container and stash in body
			cachedPortalIframe.style.display = 'none';
			document.body.appendChild(cachedPortalIframe);
		}
	}

	/**
	 * Initializes the portal by either restoring a cached iframe
	 * or injecting the script for the first time.
	 * @returns {void}
	 */
	#initializePortal() {
		const container = document.getElementById('mc-embed');
		if (!container) {
			return;
		}

		// If we have a cached iframe, just move it back into the new container
		if (cachedPortalIframe) {
			container.appendChild(cachedPortalIframe);
			cachedPortalIframe.style.display = 'block';
			return;
		}

		// Retry after a prior load if no iframe was created while the page was away.
		if (portalScriptState === 'loaded') {
			this.#injectScript();
			return;
		}

		// Otherwise, load the script if we haven't already
		if (portalScriptState === 'idle') {
			this.#injectScript();
		}
	}

	/**
	 * @returns {void}
	 * @throws {Error}
	 */
	// eslint-disable-next-line class-methods-use-this
	#injectScript() {
		if (!conciergeSelfPortalUrl) {
			throw new Error(
				'REACT_APP_CONCIERGE_SELF_PORTAL_URL is undefined.'
			);
		}

		const existingScript = document.getElementById(
			'mooveguru-embed-script'
		);
		if (existingScript) {
			existingScript.remove();
		}

		const tenant = MoveConciergePortal.#getTenant();
		const script = document.createElement('script');

		portalScriptState = 'loading';
		script.id = 'mooveguru-embed-script';
		script.src = conciergeSelfPortalUrl;
		script.setAttribute('data-company', tenant);
		script.onload = () => {
			portalScriptState = 'loaded';
		};
		script.onerror = () => {
			portalScriptState = 'idle';
			script.remove();
		};

		document.body.appendChild(script);
	}

	/**
	 * @returns {string}
	 */
	static #getTenant() {
		const host = window.location.hostname.toLowerCase();
		if (host.includes('simplyhome')) {
			if (host.includes('era')) {
				return 'shera';
			}

			return 'sh';
		}

		return 'hk';
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		return (
			<main className="move-concierge-portal">
				{this.props.withTitle !== false ? (
					<header className="mt-10">
						<h2 className="hl-ms-6 mb-4">
							{translate('homeowner.nav.main.concierge')}
						</h2>
					</header>
				) : null}
				<div data-rendered={this.props !== null} id="mc-embed" />
			</main>
		);
	}
}
